import { useCallback } from "react";
import { useGrowthBook } from "@growthbook/growthbook-react";
import config from "@Config/index";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import useIsDesktop from "hooks/useIsDesktop";
import useWhitelabel from "hooks/useWhitelabel";
import {
  ROOM_DETAILS_VARIANT,
  RoomDetailsVariant,
  setRoomDetailsVariant,
  SIDE_PANEL_MIN_VIEWPORT_WIDTH,
} from "helpers/roomDetailsTracking";

const FLAG = GROWTH_BOOK_FEATURE_FLAG_NAMES.ROOM_DETAILS_SIDE_PANEL;

/**
 * Returns a function to call from the View More Details click handler.
 *
 * The flag is evaluated here, inside the click, and never in render: GrowthBook sends
 * `experiment_viewed` when a flagged experiment is evaluated, and the test must only bucket users
 * who open room details (FR-27). `useFeatureIsOn` would expose everyone on page load.
 *
 * Resolves to `null` when the user is not part of the test (mobile, whitelabel, <1200px or
 * GrowthBook not loaded): the flag is then not evaluated, so nobody is bucketed.
 * Otherwise the arm is stored in a cookie so later lead / bookform events can carry it.
 */
const useRoomDetailsVariant = () => {
  const growthbook = useGrowthBook();
  const isDesktop = useIsDesktop();
  const { isWhitelabel } = useWhitelabel();

  return useCallback((): RoomDetailsVariant | null => {
    const fitsSidePanel =
      isDesktop &&
      !isWhitelabel &&
      typeof window !== "undefined" &&
      window.innerWidth >= SIDE_PANEL_MIN_VIEWPORT_WIDTH;
    // GrowthBook never initialises in the China build, so China skips the test and always gets
    // the side panel. No variant cookie is set, so China events are not tagged with an arm.
    if (config.IS_CHINA_APP) return fitsSidePanel ? ROOM_DETAILS_VARIANT.SIDE_PANEL : null;

    const isEligible = fitsSidePanel && Boolean(growthbook?.ready);
    if (!isEligible) return null;

    const variant = growthbook?.isOn(FLAG)
      ? ROOM_DETAILS_VARIANT.SIDE_PANEL
      : ROOM_DETAILS_VARIANT.MODAL;
    setRoomDetailsVariant(variant);
    return variant;
  }, [growthbook, isDesktop, isWhitelabel]);
};

export default useRoomDetailsVariant;
